import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { toast } from "sonner";

import {
  useGetCourseBySlugQuery,
  useStartLessonExerciseMutation,
  useSaveExerciseAnswerMutation,
  useSubmitLessonExerciseMutation
} from "@/features/api/courseApi";

import { Button } from "@/components/ui/button";
import { CardFooter } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";

import {
  ArrowRight,
  PlayCircle,
  RotateCcw,
  ArrowLeft,
} from "lucide-react";

/* ================= EXERCISES ================= */
import MultipleChoiceExercise from "@/assets/AllCourses/Exercises/MultipleChoice";
import FillInTheBlanksExercise from "@/assets/AllCourses/Exercises/FillInTheBlanks";
import PictureFillInTheBlanksExercise from "@/assets/AllCourses/Exercises/PictureFillInTheBlanks";
import MatchingExercise from "@/assets/AllCourses/Exercises/Matching";
import PronunciationExercise from "@/assets/AllCourses/Exercises/Pronunciation";
import ListeningExercise from "@/assets/AllCourses/Exercises/Listening";
import DragAndDropExercise from "@/assets/AllCourses/Exercises/DragAndDrop";

/* ================= CONSTANTS ================= */
const PASS_PERCENT = 30;
const SCROLL_OFFSET = 140;

/* ✅ SAFE MAP (OUTSIDE COMPONENT) */
const exerciseComponents = {
  "multiple-choice": MultipleChoiceExercise,
  "fill-in-the-blanks": FillInTheBlanksExercise,
  "picture-fill-in-the-blanks": PictureFillInTheBlanksExercise,
  matching: MatchingExercise,
  pronunciation: PronunciationExercise,
  listening: ListeningExercise,
  "drag-and-drop": DragAndDropExercise,
  "exercise-section": () => null
};

export default function CourseLearningPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const exerciseRefs = useRef([]);
  const lastSavedAnswersRef = useRef({});

  const [currentLessonId, setCurrentLessonId] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [answers, setAnswers] = useState({});
  const [attemptId, setAttemptId] = useState(null);

  const [showResult, setShowResult] = useState(false);
  const [retryMode, setRetryMode] = useState(false);
  const [scorePercent, setScorePercent] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, isLoading } = useGetCourseBySlugQuery(slug);

  const [startAttempt] = useStartLessonExerciseMutation();
  const [saveAnswer] = useSaveExerciseAnswerMutation();
  const [submitAttempt] = useSubmitLessonExerciseMutation();

  const course = data?.course;
  const lessons = course?.lessons || [];

  const currentLesson = useMemo(() => {
    return lessons.find(l => l._id === currentLessonId) || lessons[0];
  }, [currentLessonId, lessons]);

  const exercises = currentLesson?.exercises || [];

  /* ================= START / RESUME ================= */
  useEffect(() => {
    if (!currentLesson?._id) return;

    const key = `attempt_${currentLesson._id}`;
    const stored = localStorage.getItem(key);

    const init = async () => {
      try {
        if (stored) {
          setAttemptId(stored);
          return;
        }

        const res = await startAttempt({
          lessonId: currentLesson._id
        }).unwrap();

        setAttemptId(res?.attemptId);
        localStorage.setItem(key, res?.attemptId);

      } catch (e) {
        console.error("Start attempt error:", e);
      }
    };

    init();
  }, [currentLesson?._id]);

  /* ================= SCROLL TRACK ================= */
  useEffect(() => {
    const onScroll = () => {
      let active = 0;
      exerciseRefs.current.forEach((el, i) => {
        if (el && el.getBoundingClientRect().top - SCROLL_OFFSET <= 0) {
          active = i;
        }
      });
      setCurrentIndex(active);
    };

    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [exercises]);

  /* ================= AUTO SAVE ================= */
  useEffect(() => {
    if (!attemptId || showResult) return;

    const interval = setInterval(async () => {
      try {
        const changed = Object.entries(answers).filter(([id, val]) => {
          const prev = lastSavedAnswersRef.current[id];
          return !prev || prev.answer !== val.answer;
        });

        if (!changed.length) return;

        await Promise.all(
          changed.map(([id, val]) =>
            saveAnswer({
              attemptId,
              exerciseId: id,
              answer: val.answer,
              correct: val.correct
            })
          )
        );

        lastSavedAnswersRef.current = { ...answers };

      } catch (e) {
        console.error("Autosave error:", e);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [answers, attemptId, showResult]);

  /* ================= ANSWER ================= */
  const handleComplete = useCallback((id, answer, correct) => {
    setAnswers(prev => ({
      ...prev,
      [id]: { answer, correct }
    }));
  }, []);

  /* ================= SUBMIT ================= */
  const submit = async () => {
    if (isSubmitting) return;

    const realExercises = exercises.filter(e => e.type !== "exercise-section");

    if (Object.keys(answers).length !== realExercises.length) {
      return toast.error("Complete all questions");
    }

    try {
      setIsSubmitting(true);

      const payload = Object.entries(answers).map(([id, val]) => ({
        exerciseId: id,
        answer: val.answer,
        correct: val.correct
      }));

      const res = await submitAttempt({
        attemptId,
        answers: payload
      }).unwrap();

      const score = res?.scorePercent || 0;

      setScorePercent(score);
      setShowResult(true);
      setRetryMode(score < PASS_PERCENT);

      if (score >= PASS_PERCENT) {
        localStorage.removeItem(`attempt_${currentLesson._id}`);
      }

    } catch (e) {
      console.error("Submit error:", e);
      toast.error("Submit failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ================= RETRY WRONG ================= */
  const retryLesson = () => {
    const wrongOnly = Object.fromEntries(
      Object.entries(answers).filter(([_, v]) => !v.correct)
    );

    setAnswers(wrongOnly);
    setShowResult(false);
    setRetryMode(false);
  };

  /* ================= CONTINUE ================= */
  const continueLesson = () => {
    const idx = lessons.findIndex(l => l._id === currentLesson._id);
    const next = lessons[idx + 1];

    if (next) {
      setCurrentLessonId(next._id);
      setAnswers({});
      setShowResult(false);
    } else {
      navigate(`/courses/${slug}/result`);
    }
  };

  if (isLoading) return <Skeleton className="h-40 w-full" />;

  return (
    <div className="flex gap-6 p-6">

      {/* TOC */}
      <aside className="w-80 bg-white p-6 rounded-xl shadow">
        <h2 className="font-bold text-xl mb-6">{course?.title}</h2>

        <Progress
          value={
            exercises.length
              ? Math.round((Object.keys(answers).length / exercises.length) * 100)
              : 0
          }
          className="mb-4"
        />

        {exercises.map((ex, i) => (
          <button
            key={i}
            onClick={() =>
              document.getElementById(`exercise-${i}`)?.scrollIntoView({
                behavior: "smooth"
              })
            }
            className={`block w-full text-left p-3 rounded mb-2
              ${currentIndex === i
                ? "bg-blue-100 text-blue-700 font-semibold"
                : "hover:bg-gray-100"}
            `}
          >
            <PlayCircle className="inline w-4 mr-2" />
            {ex.title || `Exercise ${i + 1}`}
          </button>
        ))}
      </aside>

      {/* MAIN */}
      <main className="flex-1">
        {exercises.map((ex, i) => {
          const Comp = exerciseComponents[ex.type];

          if (!Comp) {
            console.error("Unknown type:", ex.type);
            return null;
          }

          const correct = answers[ex._id]?.correct;

          return (
            <section
              key={i}
              id={`exercise-${i}`}
              ref={el => (exerciseRefs.current[i] = el)}
              className="mb-14"
            >
              <h3 className="text-xl font-bold mb-6">
                {ex.title || `Exercise ${i + 1}`}
              </h3>

              {ex.details && (
                <div
                  className="mb-6"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(ex.details)
                  }}
                />
              )}

              <div className={`p-6 rounded border
                ${showResult
                  ? correct
                    ? "bg-green-50 border-green-400"
                    : "bg-red-50 border-red-400"
                  : "bg-white"}
              `}>
                <Comp
                  exercise={ex}
                  showResult={showResult}
                  onComplete={(a, c) => handleComplete(ex._id, a, c)}
                />
              </div>
            </section>
          );
        })}

        <CardFooter className="flex justify-between mt-6">

          <Button onClick={() => navigate(-1)}>
            <ArrowLeft /> Back
          </Button>

          {showResult ? (
            retryMode ? (
              <Button variant="destructive" onClick={retryLesson}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Retry Wrong
              </Button>
            ) : (
              <Button onClick={continueLesson}>
                Continue
              </Button>
            )
          ) : (
            <Button onClick={submit} disabled={isSubmitting}>
              Submit <ArrowRight />
            </Button>
          )}

        </CardFooter>
      </main>
    </div>
  );
}